import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedSection, StaggerContainer, StaggerItem } from "../../../components/animations/AnimatedElements";

const historyData = [
  {
    year: "2023",
    title: "창업 및 기술 개발",
    items: [
      "CITY-AI 주식회사 창업",
      "CCTV 영상 AI Agent 핵심 기술 개발 착수",
      "인공지능 Agent 기반 영상 분석 플랫폼 구축",
    ],
  },
  {
    year: "2024",
    title: "기술 실증 및 최초 공급",
    items: [
      "차량·사람·문자 인지판별 AI Agent 실증 진행",
      "서울교통공사 — 전국 최초 AI Agent 적용 제품 공급",
      "Unreal 기반 3D 디지털트윈 역사 모델 공급",
      "철도·공공기관 적용 사례 확보",
    ],
  },
  {
    year: "2025",
    title: "ADX 플랫폼 기반 AI 업무지원 솔루션",
    items: [
      "AI 자체 기술력 확보",
      "Digital Twin 자체 모델링 및 개발",
      "외산 AI 기술의 한계성 탈피한 ADX AI 보유",
      "철도 및 지하철용 MetroSync 솔루션",
      "공항 터미널 AirSync 솔루션",
      "도시 전체 CitySync 솔루션",
    ],
  },
  {
    year: "2026~",
    title: "고객의 니즈에 맞는 AI 업무지원 컨설팅 및 솔루션 공급",
    items: [
      "고객사별 맞춤형 AI 업무지원 컨설팅 제공",
      "ADX 플랫폼 기반 통합 솔루션 공급 확대",
      "MetroSync · AirSync · CitySync 국내외 확산",
      "산업별 특화 AI·Digital Twin 솔루션 개발",
    ],
  },
];

export default function HistorySection() {
  const [activeYear, setActiveYear] = useState("2025");

  return (
    <section id="history" className="py-28 bg-brand-darkAlt relative overflow-hidden">
      {/* Background Pattern */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(0,229,255,0.4) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection>
          <div className="section-header">
            <span className="section-accent" />
            <span className="section-label">Company History</span>
          </div>
          <h2 className="section-title mb-16">
            CITY-AI <span className="text-cyan-400">연혁</span>
          </h2>

          {/* Year Tabs - Accessible */}
          <div
            className="flex flex-wrap gap-3 mb-12"
            role="tablist"
            aria-label="연도별 연혁 선택"
          >
            {historyData.map((item) => (
              <motion.button
                key={item.year}
                role="tab"
                aria-selected={activeYear === item.year}
                aria-controls={`history-panel-${item.year}`}
                onClick={() => setActiveYear(item.year)}
                className={`px-5 py-2 rounded-md text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeYear === item.year
                    ? "bg-cyan-500 text-brand-dark shadow-glow-sm"
                    : "border border-cyan-900/40 text-gray-400 hover:border-cyan-500/50 hover:text-cyan-400"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {item.year}
              </motion.button>
            ))}
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-500/60 via-teal-500/30 to-transparent" />

            <StaggerContainer className="space-y-8" staggerDelay={0.15}>
              {historyData.map((item, idx) => (
                <StaggerItem key={item.year}>
                  <div className="relative pl-20">
                    {/* Dot */}
                    <motion.button
                      className={`absolute left-6 top-3 w-5 h-5 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 ${
                        activeYear === item.year
                          ? "bg-cyan-500 ring-4 ring-cyan-500/20"
                          : "bg-brand-darkAlt border-2 border-cyan-800"
                      }`}
                      onClick={() => setActiveYear(item.year)}
                      aria-label={`${item.year}년 연혁 보기`}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      {activeYear === item.year && (
                        <motion.div
                          className="w-2 h-2 bg-brand-dark rounded-full"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 500 }}
                        />
                      )}
                    </motion.button>

                    {/* Card */}
                    <motion.div
                      id={`history-panel-${item.year}`}
                      role="tabpanel"
                      className={`bg-brand-dark border rounded-lg p-6 cursor-pointer transition-all duration-300 ${
                        activeYear === item.year
                          ? "border-cyan-500/50 shadow-glow-sm"
                          : "border-cyan-900/20 hover:border-cyan-900/50"
                      }`}
                      onClick={() => setActiveYear(item.year)}
                      whileHover={{ x: 5 }}
                    >
                      <div className="flex items-center gap-4 mb-3">
                        <motion.span
                          className="text-2xl font-bold text-cyan-400"
                          animate={activeYear === item.year ? { scale: 1.1 } : { scale: 1 }}
                        >
                          {item.year}
                        </motion.span>
                        <span className="text-white font-semibold">{item.title}</span>
                      </div>

                      <AnimatePresence>
                        {activeYear === item.year && (
                          <motion.ul
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            {item.items.map((point, itemIdx) => (
                              <motion.li
                                key={point}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: itemIdx * 0.05 }}
                                className="flex items-start gap-2 text-gray-400 text-sm py-1"
                              >
                                <span className="mt-1.5 w-1.5 h-1.5 bg-cyan-500 rounded-full flex-shrink-0" />
                                {point}
                              </motion.li>
                            ))}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
